/**
 * MatterStalenessChart
 *
 * Replaces a per-matter bar chart that could not work: the tab filters to
 * matters at or under an hours threshold, so bar length (hours) had almost no
 * variance by construction — 20 bars all pinned at ~0 against a 2h axis.
 *
 * The signal in this data is *staleness*, not hours. Across the tenant the
 * spread is dramatic (thousands of matters untouched for over a year against a
 * few hundred worked this fortnight), so bucketing by days since last activity
 * shows the backlog the hours axis hid.
 *
 * Colour is an ordinal one-hue ramp rather than categorical hues: the buckets
 * are ordered, so the ramp lets the reader see the order in the colour itself.
 * The steps live in index.css as --ordinal-1..5 so the dark variant comes from
 * the theme blocks rather than a JS theme read: /admin renders inside V1App,
 * which has no ThemeProvider, so calling useTheme() here would throw.
 */

import { useMemo } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { UnbilledMatterRow } from "../../types/reports";

/** Upper bound in days for each band; the last is open-ended. */
const BANDS: Array<{ label: string; max: number }> = [
	{ label: "Under 15 days", max: 15 },
	{ label: "15–45 days", max: 45 },
	{ label: "45–90 days", max: 90 },
	{ label: "90 days–1 year", max: 365 },
	{ label: "Over a year", max: Number.POSITIVE_INFINITY },
];

/** Ordinal ramp; the light/dark steps are chosen by the theme blocks in index.css. */
const RAMP = [
	"var(--ordinal-1)",
	"var(--ordinal-2)",
	"var(--ordinal-3)",
	"var(--ordinal-4)",
	"var(--ordinal-5)",
];

export interface StalenessBucket {
	label: string;
	matters: number;
	index: number;
}

export function bucketByStaleness(
	rows: UnbilledMatterRow[],
): StalenessBucket[] {
	const counts = BANDS.map(() => 0);
	let never = 0;
	for (const row of rows) {
		const days = row.daysStale;
		if (days === null) {
			never += 1;
			continue;
		}
		// The last band is open-ended, so findIndex always hits; the -1 guard is
		// belt and braces if a finite bound is ever appended.
		const i = BANDS.findIndex((band) => days < band.max);
		counts[i === -1 ? BANDS.length - 1 : i] += 1;
	}
	const buckets = BANDS.map((band, index) => ({
		label: band.label,
		matters: counts[index],
		index,
	}));
	// Only shown when the tenant actually has matters with no activity at all;
	// the endpoint's inner join usually excludes them.
	if (never > 0) {
		buckets.push({
			label: "Never worked",
			matters: never,
			index: BANDS.length,
		});
	}
	return buckets;
}

function StalenessTooltip({
	active,
	payload,
	total,
}: {
	active?: boolean;
	payload?: Array<{ payload: StalenessBucket }>;
	total: number;
}) {
	const bucket = payload?.[0]?.payload;
	if (!active || !bucket) return null;
	const share = total > 0 ? Math.round((bucket.matters / total) * 100) : 0;
	return (
		<div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
			<p className="font-medium text-popover-foreground">{bucket.label}</p>
			<p className="mt-0.5 text-popover-foreground">
				{bucket.matters.toLocaleString()} matters
				<span className="text-muted-foreground"> · {share}% of shown</span>
			</p>
			<p className="mt-1 text-muted-foreground">since last time entry</p>
		</div>
	);
}

interface MatterStalenessChartProps {
	rows: UnbilledMatterRow[];
}

export default function MatterStalenessChart({
	rows,
}: MatterStalenessChartProps) {
	const buckets = useMemo(() => bucketByStaleness(rows), [rows]);
	const total = rows.length;

	return (
		<ResponsiveContainer width="100%" height={260}>
			<BarChart
				data={buckets}
				margin={{ top: 24, right: 8, bottom: 4, left: 8 }}
				barCategoryGap="28%"
			>
				<CartesianGrid
					vertical={false}
					stroke="var(--border)"
					strokeDasharray="3 3"
				/>
				<XAxis
					dataKey="label"
					tickLine={false}
					axisLine={{ stroke: "var(--border)" }}
					tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
					interval={0}
				/>
				<YAxis
					tickLine={false}
					axisLine={false}
					width={48}
					tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
					tickFormatter={(v: number) => v.toLocaleString()}
				/>
				<Tooltip
					cursor={{ fill: "var(--muted)", opacity: 0.4 }}
					content={<StalenessTooltip total={total} />}
				/>
				<Bar dataKey="matters" radius={[4, 4, 0, 0]} isAnimationActive={false}>
					{buckets.map((bucket) => (
						<Cell
							key={bucket.label}
							fill={RAMP[Math.min(bucket.index, RAMP.length - 1)]}
						/>
					))}
					{/* Direct labels: the counts are the point, and they also satisfy
					    the relief the pale end of the ramp obliges. */}
					<LabelList
						dataKey="matters"
						position="top"
						offset={8}
						fontSize={12}
						fill="var(--muted-foreground)"
						formatter={(v: unknown) =>
							typeof v === "number" && v > 0 ? v.toLocaleString() : ""
						}
					/>
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
}
